import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Shield, TrendingUp, Users, CheckCircle2, ArrowRight,
  Play, FileText, Download, ChevronRight, Star, Lock, Zap,
  Globe, Target, BarChart3, Layers, Award,
  Eye, Handshake, Scale, Clock, ChevronDown
} from 'lucide-react';
import AEDIcon from '../components/icons/AEDIcon';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const InvestorPage = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setActiveSection(id);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="investor-page">
      <div className="noise-overlay" />
      
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl border-b border-white/10 bg-background/80">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 rounded logo-icon-metallic flex items-center justify-center">
                <span className="font-display text-lg text-primary-foreground font-bold">R</span>
              </div>
              <span className="font-display text-xl font-medium tracking-tight gold-interactive">Rashii</span>
              <Badge variant="gold" className="ml-2">INVESTOR DECK</Badge>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/investor/demo')} className="border-gold-600/30">
                <Play className="w-4 h-4 mr-2" /> Watch Demo
              </Button>
              <Button variant="outline" onClick={() => navigate('/investor/deck')} className="border-gold-600/30">
                <FileText className="w-4 h-4 mr-2" /> PDF Deck
              </Button>
              <Button onClick={() => scrollToSection('contact')} className="btn-gold-metallic">
                Connect With Us
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <Badge variant="gold" className="mb-6">PRE-SEED OPPORTUNITY</Badge>
            <h1 className="font-display text-5xl md:text-7xl tracking-tight mb-6">
              The Future of<br />
              <span className="text-primary">Real Estate Intelligence</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Rashii is building the verified exchange platform where decision intelligence 
              meets real estate transactions. Part of the Subarashi Real Estate ecosystem.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button onClick={() => scrollToSection('problem')} size="lg" className="btn-gold-metallic">
                View Pitch Deck <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <Button variant="outline" size="lg" onClick={() => navigate('/')} className="border-white/20">
                <Globe className="w-5 h-5 mr-2" /> Explore Platform
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-12 border-y border-white/10 bg-secondary/20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-display text-primary mb-2">AED 917B</div>
              <div className="text-sm text-muted-foreground">Dubai Transactions (2025)</div>
              <div className="text-xs text-muted-foreground/60 mt-1">Source: DLD*</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-display text-primary mb-2">270K+</div>
              <div className="text-sm text-muted-foreground">Annual Transactions</div>
              <div className="text-xs text-muted-foreground/60 mt-1">Source: DLD*</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-display text-primary mb-2">45 Days</div>
              <div className="text-sm text-muted-foreground">Avg. Transaction Time</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-display text-primary mb-2">$1.55B</div>
              <div className="text-sm text-muted-foreground">PropTech TAM by 2030</div>
              <div className="text-xs text-muted-foreground/60 mt-1">Source: TechSci Research</div>
            </div>
          </div>
          <div className="text-center mt-6 text-xs text-muted-foreground/50">
            *Dubai Land Department Official Statistics 2025
          </div>
        </div>
      </section>

      {/* Problem & Solution */}
      <section id="problem" className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Problem */}
            <Card className="glass-card border-red-600/20">
              <CardHeader>
                <Badge className="w-fit bg-red-600/20 text-red-400 mb-4">THE PROBLEM</Badge>
                <CardTitle className="font-display text-3xl">Real Estate is Broken</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded bg-red-600/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-red-400">1</span>
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Information Asymmetry</h4>
                    <p className="text-sm text-muted-foreground">Sellers know more than buyers. Agents gatekeep critical data.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded bg-red-600/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-red-400">2</span>
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Unverified Listings</h4>
                    <p className="text-sm text-muted-foreground">Fake listings, inflated prices, and undisclosed defects plague the market.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded bg-red-600/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-red-400">3</span>
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Fragmented Process</h4>
                    <p className="text-sm text-muted-foreground">No single platform handles the full journey from search to settlement.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded bg-red-600/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-red-400">4</span>
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Emotional Decisions</h4>
                    <p className="text-sm text-muted-foreground">Buyers make million-dirham decisions based on gut feelings, not data.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Solution */}
            <Card className="glass-card border-green-600/20">
              <CardHeader>
                <Badge className="w-fit bg-green-600/20 text-green-400 mb-4">THE SOLUTION</Badge>
                <CardTitle className="font-display text-3xl">Rashii Decision Intelligence</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded bg-green-600/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Verified Exchange</h4>
                    <p className="text-sm text-muted-foreground">Every property, buyer, and seller is KYC-verified before participation.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded bg-green-600/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Rashii Score Intelligence</h4>
                    <p className="text-sm text-muted-foreground">AI-powered scoring system analyzes price fairness, location value, and market timing.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded bg-green-600/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Structured Negotiation</h4>
                    <p className="text-sm text-muted-foreground">Transparent offer/counter-offer system with deadline enforcement.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded bg-green-600/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Mandatory Settlement</h4>
                    <p className="text-sm text-muted-foreground">All transactions settle through Rashii escrow - ensuring completion.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section id="features" className="py-20 bg-secondary/20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <Badge variant="gold" className="mb-4">PLATFORM FEATURES</Badge>
            <h2 className="font-display text-4xl mb-4">Built for the UAE Market</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Rashii combines regulatory compliance with cutting-edge technology
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="glass-card border-white/10 hover:border-primary/30 transition-colors">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded bg-primary/20 flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-display text-xl mb-2">KYC Verification</h3>
                <p className="text-sm text-muted-foreground">
                  Emirates ID validation, company verification, and document authentication for all participants.
                </p>
              </CardContent>
            </Card>

            <Card className="glass-card border-white/10 hover:border-primary/30 transition-colors">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded bg-primary/20 flex items-center justify-center mb-4">
                  <Star className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-display text-xl mb-2">Rashii Score</h3>
                <p className="text-sm text-muted-foreground">
                  Proprietary algorithm analyzing 50+ factors including price trends, area development, and market timing.
                </p>
              </CardContent>
            </Card>

            <Card className="glass-card border-white/10 hover:border-primary/30 transition-colors">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded bg-primary/20 flex items-center justify-center mb-4">
                  <Eye className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-display text-xl mb-2">Viewing Intelligence</h3>
                <p className="text-sm text-muted-foreground">
                  Limited viewing slots create urgency. Buyers commit intent before viewing, reducing time-wasters.
                </p>
              </CardContent>
            </Card>

            <Card className="glass-card border-white/10 hover:border-primary/30 transition-colors">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded bg-primary/20 flex items-center justify-center mb-4">
                  <Handshake className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-display text-xl mb-2">Structured Offers</h3>
                <p className="text-sm text-muted-foreground">
                  Time-bound offers with transparent counter-offer chains. No more ghosting or endless negotiations.
                </p>
              </CardContent>
            </Card>

            <Card className="glass-card border-white/10 hover:border-primary/30 transition-colors">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded bg-primary/20 flex items-center justify-center mb-4">
                  <Scale className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-display text-xl mb-2">Escrow Settlement</h3>
                <p className="text-sm text-muted-foreground">
                  Integrated escrow ensures funds are secure. Dispute resolution built into the platform.
                </p>
              </CardContent>
            </Card>

            <Card className="glass-card border-white/10 hover:border-primary/30 transition-colors">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded bg-primary/20 flex items-center justify-center mb-4">
                  <BarChart3 className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-display text-xl mb-2">Market Analytics</h3>
                <p className="text-sm text-muted-foreground">
                  Real-time insights on pricing trends, demand patterns, and investment potential by area.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* User Journeys */}
      <section id="demo" className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <Badge variant="gold" className="mb-4">PLATFORM DEMO</Badge>
            <h2 className="font-display text-4xl mb-4">See Rashii in Action</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Experience the complete buyer and seller journeys
            </p>
          </div>

          <Tabs defaultValue="buyer" className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8">
              <TabsTrigger value="buyer">Buyer Journey</TabsTrigger>
              <TabsTrigger value="seller">Seller Journey</TabsTrigger>
            </TabsList>

            <TabsContent value="buyer">
              <div className="grid md:grid-cols-5 gap-4">
                {[
                  { step: 1, title: 'Register & Verify', desc: 'Create account, complete KYC', icon: Users },
                  { step: 2, title: 'Browse & Analyze', desc: 'View Rashii Scores, market data', icon: Eye },
                  { step: 3, title: 'Commit Intent', desc: 'Lock viewing slot with commitment', icon: Lock },
                  { step: 4, title: 'Make Offer', desc: 'Submit structured, time-bound offer', icon: AEDIcon },
                  { step: 5, title: 'Settle & Own', desc: 'Complete via Rashii escrow', icon: CheckCircle2 },
                ].map((item) => (
                  <Card key={item.step} className="glass-card border-white/10 text-center">
                    <CardContent className="p-6">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-3">
                        <item.icon className="w-5 h-5 text-primary" />
                      </div>
                      <Badge variant="outline" className="mb-2">Step {item.step}</Badge>
                      <h4 className="font-medium text-sm mb-1">{item.title}</h4>
                      <p className="text-xs text-muted-foreground">{item.desc}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <div className="text-center mt-8">
                <Button onClick={() => navigate('/register')} className="btn-gold-metallic">
                  Try Buyer Journey <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="seller">
              <div className="grid md:grid-cols-5 gap-4">
                {[
                  { step: 1, title: 'Register & KYC', desc: 'Verify identity and ownership', icon: Shield },
                  { step: 2, title: 'List Property', desc: 'Upload docs, set pricing', icon: Building2 },
                  { step: 3, title: 'Get Rashii Score', desc: 'AI-generated market analysis', icon: Star },
                  { step: 4, title: 'Manage Viewings', desc: 'Control access, review buyers', icon: Clock },
                  { step: 5, title: 'Accept & Settle', desc: 'Review offers, complete sale', icon: Handshake },
                ].map((item) => (
                  <Card key={item.step} className="glass-card border-white/10 text-center">
                    <CardContent className="p-6">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-3">
                        <item.icon className="w-5 h-5 text-primary" />
                      </div>
                      <Badge variant="outline" className="mb-2">Step {item.step}</Badge>
                      <h4 className="font-medium text-sm mb-1">{item.title}</h4>
                      <p className="text-xs text-muted-foreground">{item.desc}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <div className="text-center mt-8">
                <Button onClick={() => navigate('/register')} className="btn-gold-metallic">
                  Try Seller Journey <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Business Model */}
      <section id="business" className="py-20 bg-secondary/20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <Badge variant="gold" className="mb-4">BUSINESS MODEL</Badge>
            <h2 className="font-display text-4xl mb-4">Multiple Revenue Streams</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="glass-card border-white/10">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-display text-primary mb-2">2-5%</div>
                <h4 className="font-medium mb-2">Transaction Fee</h4>
                <p className="text-xs text-muted-foreground">
                  2% (Secondary Market) | 5% (Off-Plan)
                </p>
              </CardContent>
            </Card>

            <Card className="glass-card border-white/10">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-display text-primary mb-2">AED 500</div>
                <h4 className="font-medium mb-2">Listing Fee</h4>
                <p className="text-xs text-muted-foreground">
                  Per property listing with Rashii Score analysis
                </p>
              </CardContent>
            </Card>

            <Card className="glass-card border-white/10">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-display text-primary mb-2">Premium</div>
                <h4 className="font-medium mb-2">Intelligence Access</h4>
                <p className="text-xs text-muted-foreground">
                  Subscription for advanced market analytics
                </p>
              </CardContent>
            </Card>

            <Card className="glass-card border-white/10">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-display text-primary mb-2">Agent</div>
                <h4 className="font-medium mb-2">Portal Fees</h4>
                <p className="text-xs text-muted-foreground">
                  Licensed agent subscriptions and lead fees
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* The Ecosystem */}
      <section id="ecosystem" className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <Badge variant="gold" className="mb-4">THE ECOSYSTEM</Badge>
            <h2 className="font-display text-4xl mb-4">Part of Subarashi Real Estate</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Rashii is the technology arm of a vertically integrated real estate ecosystem
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="glass-card border-primary/30">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-display text-2xl mb-2">Subarashi Real Estate</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Traditional brokerage with verified agents and premium service
                </p>
                <Badge variant="outline">Active</Badge>
              </CardContent>
            </Card>

            <Card className="glass-card border-primary/30 ring-2 ring-primary/50">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full logo-icon-metallic flex items-center justify-center mx-auto mb-4">
                  <span className="font-display text-2xl text-primary-foreground font-bold">R</span>
                </div>
                <h3 className="font-display text-2xl mb-2">Rashii Platform</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Decision intelligence technology powering verified transactions
                </p>
                <Badge variant="gold">Building</Badge>
              </CardContent>
            </Card>

            <Card className="glass-card border-white/10">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-secondary/50 flex items-center justify-center mx-auto mb-4">
                  <Layers className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="font-display text-2xl mb-2">Future Ventures</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Property management, mortgage marketplace, fractional ownership
                </p>
                <Badge variant="outline">Planned</Badge>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section id="roadmap" className="py-20 bg-secondary/20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <Badge variant="gold" className="mb-4">ROADMAP</Badge>
            <h2 className="font-display text-4xl mb-4">Path to Market Leadership</h2>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <Card className="glass-card border-green-600/30">
              <CardContent className="p-6">
                <Badge className="bg-green-600/20 text-green-400 mb-4">Q1 2025</Badge>
                <h4 className="font-display text-lg mb-2">Platform Launch</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Core buyer/seller journeys</li>
                  <li>• KYC verification system</li>
                  <li>• Rashii Score v1</li>
                  <li>• Basic analytics</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="glass-card border-primary/30">
              <CardContent className="p-6">
                <Badge variant="gold" className="mb-4">Q2 2025</Badge>
                <h4 className="font-display text-lg mb-2">Market Expansion</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Agent portal launch</li>
                  <li>• Escrow integration</li>
                  <li>• UAE Pass login</li>
                  <li>• Mobile app beta</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="glass-card border-white/10">
              <CardContent className="p-6">
                <Badge variant="outline" className="mb-4">Q3-Q4 2025</Badge>
                <h4 className="font-display text-lg mb-2">Intelligence Suite</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Advanced market analytics</li>
                  <li>• DLD API integration</li>
                  <li>• Predictive pricing</li>
                  <li>• Investment tools</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="glass-card border-white/10">
              <CardContent className="p-6">
                <Badge variant="outline" className="mb-4">2026</Badge>
                <h4 className="font-display text-lg mb-2">Scale & Expand</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• GCC market entry</li>
                  <li>• Smart contracts</li>
                  <li>• Tokenized records</li>
                  <li>• Mortgage marketplace</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Investment Ask */}
      <section id="investment" className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          <Card className="glass-card border-primary/30 gold-glow">
            <CardContent className="p-12 text-center">
              <Badge variant="gold" className="mb-6">SEED ROUND</Badge>
              <h2 className="font-display text-4xl mb-4">Join the Journey</h2>
              <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
                We're raising our seed round to scale operations, expand market presence, 
                and capture the UAE real estate technology opportunity.
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
                <div>
                  <div className="text-3xl font-display text-primary mb-1">$5M</div>
                  <div className="text-sm text-muted-foreground">Target Raise</div>
                  <div className="text-xs text-muted-foreground/70">(AED 18.4M)</div>
                </div>
                <div>
                  <div className="text-3xl font-display text-primary mb-1">$3M</div>
                  <div className="text-sm text-muted-foreground">Minimum Entry</div>
                  <div className="text-xs text-muted-foreground/70">(AED 11M)</div>
                </div>
                <div>
                  <div className="text-3xl font-display text-primary mb-1">36 mo</div>
                  <div className="text-sm text-muted-foreground">Runway</div>
                </div>
                <div>
                  <div className="text-3xl font-display text-primary mb-1">Equity</div>
                  <div className="text-sm text-muted-foreground">Investment Type</div>
                </div>
              </div>

              <h4 className="font-medium mb-6 text-lg">Use of Funds</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-sm mb-8">
                <div className="p-4 rounded bg-secondary/30">
                  <div className="text-primary font-medium text-lg">30%</div>
                  <div className="text-muted-foreground text-xs">Marketing & Brand</div>
                </div>
                <div className="p-4 rounded bg-secondary/30">
                  <div className="text-primary font-medium text-lg">25%</div>
                  <div className="text-muted-foreground text-xs">Technology Dev</div>
                </div>
                <div className="p-4 rounded bg-secondary/30">
                  <div className="text-primary font-medium text-lg">15%</div>
                  <div className="text-muted-foreground text-xs">Operations</div>
                </div>
                <div className="p-4 rounded bg-secondary/30">
                  <div className="text-primary font-medium text-lg">12%</div>
                  <div className="text-muted-foreground text-xs">Banking Setup</div>
                </div>
                <div className="p-4 rounded bg-secondary/30">
                  <div className="text-primary font-medium text-lg">10%</div>
                  <div className="text-muted-foreground text-xs">RE Financing</div>
                </div>
                <div className="p-4 rounded bg-secondary/30">
                  <div className="text-primary font-medium text-lg">8%</div>
                  <div className="text-muted-foreground text-xs">Compliance</div>
                </div>
              </div>

              <div className="text-left max-w-2xl mx-auto bg-secondary/20 rounded-lg p-6">
                <h5 className="font-medium mb-3 text-primary">Fund Allocation Details</h5>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li className="flex gap-2"><span className="text-primary">•</span> Marketing & Brand Awareness campaigns across UAE</li>
                  <li className="flex gap-2"><span className="text-primary">•</span> Technology infrastructure, AI/ML development & platform scaling</li>
                  <li className="flex gap-2"><span className="text-primary">•</span> Running costs, team expansion & operational upkeep</li>
                  <li className="flex gap-2"><span className="text-primary">•</span> Banking facilities onboarding & payment integrations</li>
                  <li className="flex gap-2"><span className="text-primary">•</span> Real estate operations financing & holiday homes launch</li>
                  <li className="flex gap-2"><span className="text-primary">•</span> Regulatory compliance & licensing</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-20 bg-secondary/20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Badge variant="gold" className="mb-6">GET IN TOUCH</Badge>
          <h2 className="font-display text-4xl mb-4">Let's Talk</h2>
          <p className="text-muted-foreground mb-8">
            Interested in learning more about Rashii and the Subarashi ecosystem?
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button size="lg" className="btn-gold-metallic">
              <a href="mailto:invest@rashii.ae" className="flex items-center">
                Schedule Meeting <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
            <Button size="lg" variant="outline" className="border-white/20">
              <a href="mailto:admin@subarashirealestate.com" className="flex items-center">
                <FileText className="w-5 h-5 mr-2" /> Request Full Deck
              </a>
            </Button>
          </div>

          <div className="text-sm text-muted-foreground">
            <p>Email: admin@subarashirealestate.com</p>
            <p>Dubai, United Arab Emirates</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded logo-icon-metallic flex items-center justify-center">
                <span className="font-display text-sm text-primary-foreground font-bold">R</span>
              </div>
              <span className="font-display text-lg gold-interactive">Rashii</span>
              <span className="text-muted-foreground text-sm">| Part of Subarashi Real Estate</span>
            </div>
            <p className="text-xs text-muted-foreground">
              © 2024 Rashii. Confidential Investor Materials.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default InvestorPage;
